import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { BookOpen, Users, Award, CheckCircle, ArrowRight, FileText, Clock, RefreshCw, Play, Camera, Upload, X, Image } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getStates, getDistricts, getBlocks } from "../data/indiaLocations";
import wwgsLogo from "../assets/wwags-logo.jpg";

// User types for surveys
const USER_TYPES = [
  { value: "govt_secretariat", label: "Govt. Secretariat Staff" },
  { value: "govt_mts", label: "Govt. MTS Staff" },
  { value: "svep_mentor", label: "SVEP Mentor" },
  { value: "svep_crp_ep", label: "SVEP CRP-EPs" },
  { value: "svep_cep_coordinator", label: "SVEP CEP-EPs Coordinators" },
  { value: "staff_promotion", label: "Staff Promotion Exam" },
  { value: "vo_member", label: "VOs (Village Organizations)" },
  { value: "clf_member", label: "CLF (Cluster Level Federation)" },
  { value: "shg_member", label: "SHGs (Self Help Groups)" },
  { value: "other", label: "Others" }
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [showRegistration, setShowRegistration] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    registration_number: "",
    mobile_number: "",
    state: "",
    district: "",
    block: "",
    gram_panchayat: "",
    user_type: "svep_crp_ep",
    photo: null
  });
  const [loading, setLoading] = useState(false);
  const [waitingApproval, setWaitingApproval] = useState(false);
  const [candidateData, setCandidateData] = useState(null);
  const [isApproved, setIsApproved] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [secondaryLanguage, setSecondaryLanguage] = useState("Assamese");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const approvalCheckInterval = useRef(null);

  // Auto-sync approval status every 5 seconds when waiting
  useEffect(() => {
    // Fetch global secondary language for instructions display
    axios.get(`${API}/admin/exam-settings`)
      .then((res) => {
        if (res.data?.secondary_language) setSecondaryLanguage(res.data.secondary_language);
      })
      .catch(() => {});
  }, []);

  // Auto-sync approval status every 5 seconds when waiting
  useEffect(() => {
    if (waitingApproval && candidateData?.id) {
      // Start polling for approval status
      approvalCheckInterval.current = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/candidate/check-approval/${candidateData.id}`);
          if (response.data.approved) {
            setIsApproved(true);
            clearInterval(approvalCheckInterval.current);
            toast.success("You have been approved! You can now start the exam.");
          }
        } catch (error) {
          console.error("Error checking approval:", error);
        }
      }, 5000); // Check every 5 seconds
    }

    return () => {
      if (approvalCheckInterval.current) {
        clearInterval(approvalCheckInterval.current);
      }
    };
  }, [waitingApproval, candidateData]);

  const handleStartApprovedExam = () => {
    localStorage.setItem("candidate", JSON.stringify({ ...candidateData, approved: true }));
    navigate("/exam");
  };

  const states = getStates();
  const districts = getDistricts(formData.state);
  const blocks = getBlocks(formData.state, formData.district);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStateChange = (value) => {
    if (value === "__manual__") {
      setManualEntry(true);
      setFormData({ ...formData, state: "", district: "", block: "" });
    } else {
      setFormData({ ...formData, state: value, district: "", block: "" });
    }
  };

  const handleDistrictChange = (value) => {
    setFormData({ ...formData, district: value, block: "" });
  };

  const handleBlockChange = (value) => {
    setFormData({ ...formData, block: value });
  };

  const handleManualEntryToggle = (checked) => {
    setManualEntry(checked);
    if (checked) {
      setFormData({ ...formData, state: "", district: "", block: "" });
    }
  };

  // Camera functions for passport photo
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 }
        } 
      });
      setCameraStream(stream);
      setShowCamera(true);
      
      // Wait for videoRef to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error("Camera error:", error);
      toast.error("Unable to access camera. Please check permissions or try uploading a photo instead.");
    }
  };

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  }, [cameraStream]);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert to base64
    const photoData = canvas.toDataURL('image/jpeg', 0.8);
    setFormData({ ...formData, photo: photoData });
    
    // Stop camera
    stopCamera();
    toast.success("Photo captured successfully!");
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData({ ...formData, photo: e.target.result });
      toast.success("Photo uploaded successfully!");
    };
    reader.onerror = () => {
      toast.error("Failed to read the image file");
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setFormData({ ...formData, photo: null });
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  const handleStartExam = async () => {
    if (!formData.name.trim() || !formData.registration_number.trim()) {
      toast.error("Please fill in your name and registration number");
      return;
    }
    if (!formData.state.trim() || !formData.district.trim() || !formData.block.trim()) {
      toast.error("Please enter your State, District, and Block");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/register`, formData);
      localStorage.setItem("candidate", JSON.stringify(response.data));
      setCandidateData(response.data);
      
      // Check if candidate is approved
      if (response.data.approved) {
        toast.success("Registration successful! Starting exam...");
        navigate("/exam");
      } else {
        // Show waiting for approval screen
        setWaitingApproval(true);
        setShowRegistration(false);
        toast.info("Registration successful! Please wait for admin approval.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const checkApprovalStatus = async () => {
    if (!candidateData?.id) return;
    
    try {
      const response = await axios.get(`${API}/candidate/check-approval/${candidateData.id}`);
      if (response.data.approved) {
        toast.success("You have been approved! Starting exam...");
        localStorage.setItem("candidate", JSON.stringify({...candidateData, approved: true}));
        navigate("/exam");
      } else {
        toast.info("Still waiting for admin approval. Please check back later.");
      }
    } catch (error) {
      toast.error("Failed to check approval status");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 no-print">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <img 
              src={wwgsLogo} 
              alt="WWAGS Logo" 
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain rounded-full border border-emerald-200 flex-shrink-0"
              data-testid="header-logo"
            />
            <div className="min-w-0">
              <h1 className="text-sm sm:text-base lg:text-xl font-bold text-emerald-900 truncate sm:whitespace-normal leading-tight">
                Wangjing Women and Girls Society
              </h1>
              <p className="text-[10px] sm:text-xs text-gray-500">(WWAGS), Manipur</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => navigate("/admin")}
            data-testid="admin-login-btn"
            className="text-gray-600 hover:text-emerald-700 text-xs sm:text-sm px-2 sm:px-4 flex-shrink-0"
          >
            Admin Login
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-8 sm:py-12 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8 order-2 lg:order-1">
              <div className="space-y-3 sm:space-y-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs sm:text-sm font-medium">
                  <Award className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" /> Online Exam Portal
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-emerald-900 leading-tight tracking-tight">
                  Online Examination Portal
                </h1>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  A platform to conduct online exams for Govt. Secretariat Staff, Govt. MTS Staff, 
                  SVEP Mentors, SVEP CRP-EPs, SVEP CEP-EPs Coordinators, Staff Promotion Exam, 
                  VOs, CLFs, SHGs, and others.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button 
                  size="lg" 
                  onClick={() => setShowRegistration(true)}
                  data-testid="start-exam-btn"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full sm:w-auto"
                >
                  Register & Start Exam <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="relative order-1 lg:order-2">
              <img 
                src="https://images.unsplash.com/photo-1760720962365-6ae03435c02a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB3cml0aW5nJTIwcGFwZXIlMjBkZXNrJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3NzI3NjgxNDd8MA&ixlib=rb-4.1.0&q=85"
                alt="Person writing exam"
                className="rounded-2xl shadow-2xl w-full object-cover max-h-[280px] sm:max-h-[400px] lg:max-h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Exam Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold text-emerald-800 mb-4">Exam Structure</h2>
            <p className="text-gray-600">The examination consists of three sections</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-l-4 border-l-emerald-600 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-emerald-700" />
                </div>
                <CardTitle className="text-lg">Section I</CardTitle>
                <CardDescription>G.K, General English, Reasoning</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-emerald-700">25 Questions</p>
                <p className="text-sm text-gray-500">Multiple Choice (2 marks each)</p>
                <p className="text-xs text-emerald-600 mt-1">Total: 50 marks</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-amber-500 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-amber-700" />
                </div>
                <CardTitle className="text-lg">Section II</CardTitle>
                <CardDescription>Mathematics</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-amber-600">20 Questions</p>
                <p className="text-sm text-gray-500">Multiple Choice (2 marks each)</p>
                <p className="text-xs text-amber-600 mt-1">Total: 40 marks (40% to qualify)</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-teal-500 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-teal-700" />
                </div>
                <CardTitle className="text-lg">Section III</CardTitle>
                <CardDescription>Subjective Questions</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-teal-600">2 Questions</p>
                <p className="text-sm text-gray-500">Answer any ONE (Min 250 words)</p>
                <p className="text-xs text-teal-600 mt-1">Total: 10 marks</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Instructions */}
      <section className="py-10 sm:py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                Important Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3 text-gray-700 text-sm sm:text-base">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">1</span>
                  <span>All MCQ questions are mandatory. Answer all questions before submitting.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">2</span>
                  <span>For Section III, choose and answer <strong>any ONE</strong> question out of the two options provided.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">3</span>
                  <span>Subjective answer should be minimum 250 words. It carries 10 marks.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">4</span>
                  <span><strong>Section II (Math)</strong> requires minimum 40% (16 out of 40 marks) to qualify.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">5</span>
                  <span><strong>Overall</strong> minimum 40% (40 out of 100 marks) required to qualify.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">6</span>
                  <span>Questions are available in both English and {secondaryLanguage}.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 text-sm font-medium">7</span>
                  <span>Once submitted, you cannot modify your answers.</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer - Added extra padding for mobile to avoid watermark */}
      <footer className="py-8 pb-24 sm:pb-8 bg-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-emerald-100 font-semibold">Wangjing Women and Girls Society (WWAGS)</p>
          <p className="text-sm text-emerald-300 mt-2">Manipur | CRP-EP Examination Portal</p>
          <div className="mt-4 pt-4 border-t border-emerald-700">
            <p className="text-xs text-emerald-400">
              Portal Developed by <span className="text-emerald-200 font-medium">Institute of Skill Development and Entrepreneurship (ISDE), Manipur</span>
            </p>
            <p className="text-xs text-emerald-500 mt-1">
              (An Institute run under Shri Suvakumar Singh Memorial Trust)
            </p>
          </div>
        </div>
      </footer>

      {/* Registration Dialog */}
      <Dialog open={showRegistration} onOpenChange={setShowRegistration}>
        <DialogContent className="w-[95vw] max-w-md sm:max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-emerald-800">Candidate Registration</DialogTitle>
            <DialogDescription>
              Enter your details to begin the examination
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* User Type Selection */}
            <div className="space-y-2">
              <Label>User Type *</Label>
              <Select 
                value={formData.user_type} 
                onValueChange={(value) => setFormData({...formData, user_type: value})}
              >
                <SelectTrigger data-testid="user-type-select">
                  <SelectValue placeholder="Select User Type" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {USER_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                name="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleInputChange}
                data-testid="candidate-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="registration_number">Registration Number *</Label>
              <Input
                id="registration_number"
                name="registration_number"
                placeholder="Enter your registration number"
                value={formData.registration_number}
                onChange={handleInputChange}
                data-testid="registration-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobile_number">Mobile Number *</Label>
              <Input
                id="mobile_number"
                name="mobile_number"
                type="tel"
                placeholder="Enter your mobile number"
                value={formData.mobile_number}
                onChange={handleInputChange}
                data-testid="mobile-number-input"
                maxLength={10}
              />
            </div>

            {/* Manual Entry Toggle */}
            <div className="flex items-center space-x-2 py-2 px-3 bg-gray-50 rounded-lg">
              <Checkbox 
                id="manual-entry" 
                checked={manualEntry}
                onCheckedChange={handleManualEntryToggle}
                data-testid="manual-entry-checkbox"
              />
              <Label htmlFor="manual-entry" className="text-sm cursor-pointer">
                Enter location manually (if your location is not in the list)
              </Label>
            </div>

            {manualEntry ? (
              <>
                {/* Manual Entry Fields */}
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    name="state"
                    placeholder="Enter your state name"
                    value={formData.state}
                    onChange={handleInputChange}
                    data-testid="state-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="district">District *</Label>
                  <Input
                    id="district"
                    name="district"
                    placeholder="Enter your district name"
                    value={formData.district}
                    onChange={handleInputChange}
                    data-testid="district-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="block">Block *</Label>
                  <Input
                    id="block"
                    name="block"
                    placeholder="Enter your block name"
                    value={formData.block}
                    onChange={handleInputChange}
                    data-testid="block-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gram_panchayat_manual">Gram Panchayat Name</Label>
                  <Input
                    id="gram_panchayat_manual"
                    name="gram_panchayat"
                    placeholder="Enter your Gram Panchayat name"
                    value={formData.gram_panchayat}
                    onChange={handleInputChange}
                    data-testid="gram-panchayat-manual-input"
                  />
                </div>
              </>
            ) : (
              <>
                {/* Dropdown Fields */}
                <div className="space-y-2">
                  <Label>State *</Label>
                  <Select value={formData.state} onValueChange={handleStateChange}>
                    <SelectTrigger data-testid="state-select">
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>District *</Label>
                  <Select 
                    value={formData.district} 
                    onValueChange={handleDistrictChange}
                    disabled={!formData.state}
                  >
                    <SelectTrigger data-testid="district-select">
                      <SelectValue placeholder={formData.state ? "Select District" : "Select State first"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {districts.map((district) => (
                        <SelectItem key={district} value={district}>{district}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Block *</Label>
                  <Select 
                    value={formData.block} 
                    onValueChange={handleBlockChange}
                    disabled={!formData.district}
                  >
                    <SelectTrigger data-testid="block-select">
                      <SelectValue placeholder={formData.district ? "Select Block" : "Select District first"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {blocks.map((block) => (
                        <SelectItem key={block} value={block}>{block}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gram_panchayat">Gram Panchayat Name</Label>
                  <Input
                    id="gram_panchayat"
                    name="gram_panchayat"
                    placeholder="Enter your Gram Panchayat name"
                    value={formData.gram_panchayat}
                    onChange={handleInputChange}
                    data-testid="gram-panchayat-input"
                  />
                </div>
              </>
            )}

            {/* Passport Photo Section */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Passport Photo
              </Label>
              
              {formData.photo ? (
                <div className="relative w-32 h-40 mx-auto">
                  <img 
                    src={formData.photo} 
                    alt="Passport photo" 
                    className="w-full h-full object-cover rounded-lg border-2 border-emerald-200"
                    data-testid="passport-photo-preview"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full"
                    onClick={removePhoto}
                    data-testid="remove-photo-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2 justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={startCamera}
                    className="flex items-center gap-2"
                    data-testid="open-camera-btn"
                  >
                    <Camera className="w-4 h-4" />
                    Take Photo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2"
                    data-testid="upload-photo-btn"
                  >
                    <Upload className="w-4 h-4" />
                    Upload
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileUpload}
                    data-testid="photo-file-input"
                  />
                </div>
              )}
              <p className="text-xs text-gray-500 text-center">
                Optional: Upload a passport-size photo (max 5MB)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegistration(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleStartExam} 
              disabled={loading}
              data-testid="submit-registration-btn"
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {loading ? "Registering..." : "Register & Start Exam"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Waiting for Approval Dialog */}
      <Dialog open={waitingApproval} onOpenChange={setWaitingApproval}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isApproved ? "text-emerald-700" : "text-amber-700"}`}>
              {isApproved ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Approved! Ready to Start
                </>
              ) : (
                <>
                  <Clock className="w-5 h-5" />
                  Waiting for Approval
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {isApproved 
                ? "You have been approved by the administrator" 
                : "Your registration has been submitted successfully"}
            </DialogDescription>
          </DialogHeader>
          <div className="py-6 space-y-4">
            {isApproved ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-emerald-800 text-sm">
                  Congratulations! Your registration has been approved. You can now start the examination.
                </p>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm">
                  Your registration is pending admin approval. This page will automatically update when you are approved.
                </p>
                <div className="flex items-center gap-2 mt-2 text-amber-600 text-xs">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  <span>Auto-checking approval status...</span>
                </div>
              </div>
            )}
            {candidateData && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-sm"><strong>Name:</strong> {candidateData.name}</p>
                <p className="text-sm"><strong>Registration No:</strong> {candidateData.registration_number}</p>
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            {isApproved ? (
              <Button
                onClick={handleStartApprovedExam}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="start-approved-exam-btn"
              >
                <Play className="w-4 h-4 mr-2" /> Start Examination
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={checkApprovalStatus}
                  data-testid="check-approval-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Check Now
                </Button>
                <Button
                  onClick={() => setWaitingApproval(false)}
                >
                  Close
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onOpenChange={(open) => { if (!open) stopCamera(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-emerald-600" />
              Take Passport Photo
            </DialogTitle>
            <DialogDescription>
              Position your face within the frame and click capture
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="relative aspect-[3/4] max-w-xs mx-auto bg-gray-900 rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              {/* Guide overlay */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-40 border-2 border-dashed border-white/50 rounded-lg"></div>
              </div>
            </div>
            <canvas ref={canvasRef} className="hidden" />
            <p className="text-xs text-gray-500 text-center mt-2">
              Align your face within the dotted frame
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={stopCamera}>
              Cancel
            </Button>
            <Button
              onClick={capturePhoto}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="capture-photo-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Capture Photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
